import clsx from 'clsx';
import React, {
  FunctionComponent,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import { useDebounce, useFullscreen, useToggle } from 'react-use';
import ExpandIcon from './icons/expand-icon';
import PauseIcon from './icons/pause-icon';
import PlayIcon from './icons/play-icon';
import ReduceIcon from './icons/reduce-icon';

interface VideoControlsProps {
  currentTime: number;
  duration: number;
  fullScreenRef: RefObject<HTMLDivElement>;
  isPaused: boolean;
  pause: () => void;
  play: () => void;
  seek: (time: number) => void;
}

const VideoControls: FunctionComponent<VideoControlsProps> = ({
  currentTime,
  duration,
  fullScreenRef,
  isPaused,
  pause,
  play,
  seek,
}) => {
  const [showVideoFullScreen, toggleVideoFullScreen] = useToggle(false);
  const isVideoFullScreen = useFullscreen(fullScreenRef, showVideoFullScreen, {
    onClose: () => toggleVideoFullScreen(false),
  });

  const toggleVideoPlaying = useCallback(() => {
    if (isPaused) {
      play();
      return;
    }

    pause();
  }, [isPaused, pause, play]);

  const progressBarRef = useRef<HTMLDivElement>(null);
  const [tooltipPosition, setTooltipPosition] = useState(0);

  const [progressBarWidth, setProgressBarWidth] = useState(
    () => progressBarRef.current?.getBoundingClientRect().width || 0,
  );

  useEffect(() => {
    function storeProgressBarWidth() {
      setProgressBarWidth(
        progressBarRef.current?.getBoundingClientRect().width || 0,
      );
    }

    window.addEventListener('resize', storeProgressBarWidth);

    storeProgressBarWidth();

    return () => window.removeEventListener('resize', storeProgressBarWidth);
  }, []);

  const [, cancelTooltipPositionUpdate] = useDebounce(
    () => {
      setTooltipPosition((currentTime / duration) * progressBarWidth);
    },
    100,
    [currentTime, duration, progressBarWidth],
  );

  const onDrag = useCallback(
    (e: DraggableEvent, data: DraggableData) => {
      if (!progressBarWidth) {
        return;
      }

      cancelTooltipPositionUpdate();
      setTooltipPosition(data.x);
      seek((duration * data.x) / progressBarWidth);
    },
    [cancelTooltipPositionUpdate, duration, progressBarWidth, seek],
  );

  return (
    <div
      className={clsx(
        'absolute bottom-0 flex items-center justify-between w-full px-10 mb-10',
        isVideoFullScreen ? 'text-white' : 'text-black-lighter xl:text-white',
      )}
      ref={fullScreenRef}
    >
      <button className="p-20" onClick={toggleVideoPlaying} type="button">
        {isPaused ? <PlayIcon /> : <PauseIcon />}
      </button>
      <div className="relative flex items-center flex-1">
        <div className="relative w-full" ref={progressBarRef}>
          <div className="w-full h-2 overflow-hidden text-xs bg-gray">
            <div
              style={{
                width: `${
                  progressBarWidth
                    ? (100 * tooltipPosition) / progressBarWidth
                    : 0
                }%`,
              }}
              className={clsx(
                'absolute h-2',
                isVideoFullScreen ? 'bg-white' : 'bg-black xl:bg-white',
              )}
            />
          </div>
        </div>
        <Draggable
          axis="x"
          positionOffset={{ x: -20, y: 0 }}
          bounds={{
            left: 0,
            right: progressBarWidth,
          }}
          defaultClassName="absolute p-20"
          onDrag={onDrag}
          position={{
            x: tooltipPosition,
            y: 0,
          }}
        >
          <div>
            <div
              className={clsx(
                'w-8 h-8',
                isVideoFullScreen ? 'bg-white' : 'bg-black xl:bg-white',
              )}
            />
          </div>
        </Draggable>
      </div>
      <button className="p-20" onClick={toggleVideoFullScreen} type="button">
        {isVideoFullScreen ? <ReduceIcon /> : <ExpandIcon />}
      </button>
    </div>
  );
};

export default VideoControls;
